import type { ChangeEvent, Ref } from 'react';
import { memo } from 'react';
import type { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Loader } from 'components/common/Loader';
import { Button } from 'components/mui/common/Button';
import { FileItemSkeleton } from 'components/user/Records/components/FileUploadsItem/FileItemSkeleton';
import type { FileListEvent } from 'components/user/Records/components/FileUploadsItem';
import { FilesWrapper, InputWrapper, RootWrapper } from 'components/user/Records/components/FileUploadsItem/FileUploadsItem.styles';
import type { FileResponse } from 'types';
import { FileCardsGrid } from './FileCardsGrid';
import { MAX_FILES_PER_UPLOAD } from 'utils/file/fileUtils';
import type { DocumentEditingOptions } from 'types/documentEditing';

type FileEditModalContentProps = {
  documentEditing?: DocumentEditingOptions;
  files: FileResponse[];
  showLoader: boolean;
  isUploading: boolean;
  isDisableUpload: boolean;
  uploadError: string | null;
  inputRef: Ref<HTMLInputElement>;
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  onOpenUpload: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUploadFile: (event: ChangeEvent<HTMLInputElement> | FileListEvent) => void;
  onRemoveFile: (fileId: string) => void;
};

const FileEditModalContentBase = ({
  files,
  documentEditing,
  showLoader,
  isUploading,
  isDisableUpload,
  uploadError,
  inputRef,
  getRootProps,
  getInputProps,
  onOpenUpload,
  onUploadFile,
  onRemoveFile,
}: FileEditModalContentProps) => {
  const { t } = useTranslation();
  const isUploadLocked = isDisableUpload || isUploading;

  return (
    <>
      <RootWrapper {...getRootProps()}>
        {!isDisableUpload && (
          <Button variant="outlined" type="button" sx={{ width: '128px' }} disabled={isUploading} onClick={onOpenUpload}>
            + {t('common.upload')}
          </Button>
        )}

        <InputWrapper>
          <input
            {...getInputProps()}
            ref={inputRef}
            disabled={isUploadLocked}
            type="file"
            multiple={MAX_FILES_PER_UPLOAD > 1}
            style={{ display: 'none' }}
            onChange={onUploadFile}
          />
          <FilesWrapper>
            {showLoader ? (
              <Loader />
            ) : (
              <FileCardsGrid files={files} documentEditing={documentEditing} variant="plain" skipStorageDelete onRemove={onRemoveFile} />
            )}
            {isUploading && <FileItemSkeleton />}
          </FilesWrapper>
        </InputWrapper>
      </RootWrapper>
      {uploadError && (
        <Typography variant="body1" color="error">
          {uploadError}
        </Typography>
      )}
    </>
  );
};

export const FileEditModalContent = memo(FileEditModalContentBase);
